import { useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  InputNumber,
  Switch,
  Segmented,
  Button,
  Typography,
  Space,
  Alert,
  Modal,
  Popconfirm,
  App,
} from 'antd';
import type { SaveEmailConfigInput } from '@bom/shared';
import { useSessionStore } from '../../stores/sessionStore';
import { List, Tag } from 'antd';
import { MailOutlined, DeleteOutlined } from '@ant-design/icons';
import {
  useEmailConfig,
  useSaveEmailConfig,
  useDeleteEmailConfig,
  useTestEmail,
} from '../../api/email-config';
import {
  useMySmtpAccounts,
  useApplySmtpAccount,
  useDeleteSmtpAccount,
} from '../../api/smtp-accounts';
import { NoTenant } from '../../components/NoTenant';

export function EmailConfigPage() {
  const { message } = App.useApp();
  const tid = useSessionStore((s) => s.currentTenant?.id);
  const role = useSessionStore((s) => s.role);
  const isPlatformAdmin = useSessionStore((s) => s.user?.is_platform_admin ?? false);
  const canManage = isPlatformAdmin || role === 'admin';

  const cfg = useEmailConfig(tid);
  const save = useSaveEmailConfig(tid ?? '');
  const del = useDeleteEmailConfig(tid ?? '');
  const test = useTestEmail(tid ?? '');
  const myAccounts = useMySmtpAccounts();
  const applyAccount = useApplySmtpAccount(tid ?? '');
  const delAccount = useDeleteSmtpAccount();
  const [form] = Form.useForm<SaveEmailConfigInput>();
  const hasPassword = !!cfg.data?.has_password;
  const authType = Form.useWatch('auth_type', form) ?? 'password';
  const credLabel = authType === 'auth_code' ? 'SMTP 授权码' : 'SMTP 密码';

  // 加密方式：由 smtp_secure + smtp_ignore_tls 两个布尔派生三态
  const secure = Form.useWatch('smtp_secure', form);
  const ignoreTls = Form.useWatch('smtp_ignore_tls', form);
  const encMode: 'ssl' | 'starttls' | 'none' = secure ? 'ssl' : ignoreTls ? 'none' : 'starttls';
  const setEncMode = (m: 'ssl' | 'starttls' | 'none') => {
    form.setFieldsValue({
      smtp_secure: m === 'ssl',
      smtp_ignore_tls: m === 'none',
    });
  };

  useEffect(() => {
    if (cfg.data) {
      form.setFieldsValue({
        from_email: cfg.data.from_email,
        from_name: cfg.data.from_name,
        smtp_host: cfg.data.smtp_host,
        smtp_port: cfg.data.smtp_port,
        smtp_secure: cfg.data.smtp_secure,
        smtp_ignore_tls: cfg.data.smtp_ignore_tls ?? false,
        smtp_user: cfg.data.smtp_user,
        auth_type: cfg.data.auth_type ?? 'password',
        reply_to: cfg.data.reply_to,
      });
    }
  }, [cfg.data, form]);

  if (!tid) return <NoTenant />;

  const onSave = () => {
    form.validateFields().then((v) => {
      // 未填密码且已设置过 → 不传 smtp_pass（沿用已存）
      const body: SaveEmailConfigInput = { ...v };
      if (!v.smtp_pass) delete (body as { smtp_pass?: string }).smtp_pass;
      save.mutate(body, {
        onSuccess: () => message.success('发件配置已保存'),
        onError: (e) => message.error((e as Error).message || '保存失败'),
      });
    });
  };

  const onTest = () => {
    let to = '';
    Modal.confirm({
      title: '发送测试邮件',
      content: (
        <Input
          placeholder="收件邮箱"
          onChange={(e) => {
            to = e.target.value;
          }}
        />
      ),
      onOk: () =>
        test
          .mutateAsync(to)
          .then(() => message.success('测试邮件已发送，请查收'))
          .catch((e) => {
            message.error((e as Error).message || '发送失败');
            throw e;
          }),
    });
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%', maxWidth: 640 }}>
      <Typography.Title level={4} style={{ margin: 0 }}>
        发件配置（SMTP）
      </Typography.Title>
      <Alert
        type="info"
        showIcon
        message="谁负责谁发送"
        description="本产品的通知邮件用此处配置的邮箱发送。认证方式可选「密码」或「授权码」——多数企业邮/163/QQ 需在邮箱后台开启 SMTP 并使用授权码。未配置则不发送通知。"
      />

      {canManage && (myAccounts.data?.length ?? 0) > 0 && (
        <Card
          size="small"
          title={
            <Space>
              <MailOutlined />
              <span>我的发件邮箱</span>
            </Space>
          }
        >
          <Typography.Paragraph type="secondary" style={{ fontSize: 13, marginTop: 8 }}>
            你在其它产品配置过的发件邮箱可直接选用，无需重填（含密码）。仅显示你本人配置的账号。
          </Typography.Paragraph>
          <List
            size="small"
            dataSource={myAccounts.data ?? []}
            renderItem={(a) => {
              const isCurrent =
                cfg.data?.smtp_host === a.smtp_host && cfg.data?.smtp_user === a.smtp_user;
              return (
                <List.Item
                  actions={[
                    isCurrent ? (
                      <Tag color="success" key="cur">
                        当前使用
                      </Tag>
                    ) : (
                      <Button
                        key="apply"
                        type="link"
                        size="small"
                        loading={applyAccount.isPending && applyAccount.variables === a.id}
                        onClick={() =>
                          applyAccount.mutate(a.id, {
                            onSuccess: () => message.success(`已应用「${a.from_email}」到本产品`),
                            onError: (e) => message.error((e as Error).message || '应用失败'),
                          })
                        }
                      >
                        应用到本产品
                      </Button>
                    ),
                    <Popconfirm
                      key="del"
                      title="从我的邮箱簿中删除？"
                      description="仅从你的可选列表移除，不影响已应用该邮箱的产品。"
                      okText="删除"
                      okButtonProps={{ danger: true }}
                      cancelText="取消"
                      onConfirm={() =>
                        delAccount.mutate(a.id, {
                          onSuccess: () => message.success('已删除'),
                          onError: (e) => message.error((e as Error).message || '删除失败'),
                        })
                      }
                    >
                      <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>,
                  ]}
                >
                  <List.Item.Meta
                    title={
                      <Space size="small">
                        <span>
                          {a.from_name ? `${a.from_name} <${a.from_email}>` : a.from_email}
                        </span>
                        <Tag>{a.auth_type === 'auth_code' ? '授权码' : '密码'}</Tag>
                      </Space>
                    }
                    description={
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        {a.smtp_user} @ {a.smtp_host}:{a.smtp_port}
                      </Typography.Text>
                    }
                  />
                </List.Item>
              );
            }}
          />
        </Card>
      )}

      <Card loading={cfg.isLoading}>
        <Form
          form={form}
          layout="vertical"
          disabled={!canManage}
          initialValues={{
            smtp_port: 465,
            smtp_secure: true,
            smtp_ignore_tls: false,
            auth_type: 'password',
          }}
        >
          <Form.Item
            name="from_email"
            label="发件邮箱"
            rules={[{ required: true, type: 'email', message: '请输入发件邮箱' }]}
          >
            <Input placeholder="如 wang@meitong.com" />
          </Form.Item>
          <Form.Item name="from_name" label="显示名（可选）">
            <Input placeholder="如 美通-王工 / S1项目" />
          </Form.Item>
          <Space>
            <Form.Item
              name="smtp_host"
              label="SMTP 主机"
              rules={[{ required: true, message: '必填' }]}
            >
              <Input placeholder="smtp.exmail.qq.com" style={{ width: 260 }} />
            </Form.Item>
            <Form.Item name="smtp_port" label="端口" rules={[{ required: true, message: '必填' }]}>
              <InputNumber min={1} max={65535} style={{ width: 110 }} />
            </Form.Item>
          </Space>
          {/* 加密方式由 smtp_secure + smtp_ignore_tls 派生，两个布尔用隐藏项存储 */}
          <Form.Item name="smtp_secure" hidden valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="smtp_ignore_tls" hidden valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item label="加密方式">
            <Segmented
              value={encMode}
              onChange={(v) => setEncMode(v as 'ssl' | 'starttls' | 'none')}
              disabled={!canManage}
              options={[
                { label: 'SSL（465）', value: 'ssl' },
                { label: 'STARTTLS（587）', value: 'starttls' },
                { label: '不加密 / 明文', value: 'none' },
              ]}
            />
          </Form.Item>
          <Typography.Text
            type="secondary"
            style={{ display: 'block', marginTop: -8, fontSize: 12 }}
          >
            常规邮箱用 <b>SSL（端口 465）</b> 或 <b>STARTTLS（端口 587）</b>。
            若邮件服务器在境内、加密连接被链路阻断而明文可通（部分自建/企业邮），才用
            <b>「不加密 / 明文」</b>；⚠️ 此模式下密码与正文以明文传输，请仅在必要时使用。
          </Typography.Text>
          <Form.Item
            name="smtp_user"
            label="SMTP 用户名"
            rules={[{ required: true, message: '必填' }]}
          >
            <Input placeholder="多为邮箱全名" />
          </Form.Item>
          <Form.Item name="auth_type" label="认证方式">
            <Segmented
              options={[
                { label: '密码', value: 'password' },
                { label: '授权码', value: 'auth_code' },
              ]}
            />
          </Form.Item>
          <Form.Item
            name="smtp_pass"
            label={credLabel}
            rules={hasPassword ? [] : [{ required: true, message: `首次配置必填${credLabel}` }]}
            extra={
              hasPassword
                ? '已设置；留空表示不修改'
                : authType === 'auth_code'
                  ? '邮箱后台生成的「授权码/应用专用密码」'
                  : '邮箱登录密码'
            }
          >
            <Input.Password
              visibilityToggle={false}
              placeholder={hasPassword ? '••••••（不改请留空）' : credLabel}
            />
          </Form.Item>
          <Form.Item name="reply_to" label="回复地址（可选）">
            <Input placeholder="reply@..." />
          </Form.Item>
          {canManage && (
            <Space>
              <Button type="primary" loading={save.isPending} onClick={onSave}>
                保存
              </Button>
              <Button disabled={!hasPassword} onClick={onTest}>
                发送测试邮件
              </Button>
              {hasPassword && (
                <Popconfirm
                  title="清除本产品的发件配置？"
                  description="清除后本产品将不再发送通知邮件，可重新配置。"
                  okText="清除"
                  okButtonProps={{ danger: true }}
                  cancelText="取消"
                  onConfirm={() =>
                    del.mutate(undefined, {
                      onSuccess: () => {
                        message.success('已清除发件配置');
                        form.resetFields();
                      },
                      onError: (e) => message.error((e as Error).message || '清除失败'),
                    })
                  }
                >
                  <Button danger loading={del.isPending}>
                    清除配置
                  </Button>
                </Popconfirm>
              )}
            </Space>
          )}
        </Form>
      </Card>
    </Space>
  );
}
